import { getRouteApi } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'

import { BackgroundsPage, type BackgroundsSearchState } from '@/features/backgrounds/BackgroundsPage'

/**
 * `/backgrounds?q=&world=&needs_look=true` — every place a story can happen, searchable, by world,
 * or only the ones that need a look. Unknown values are dropped by the lenient schema.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const backgroundsSearchSchema = z.object({
  q: lenient(z.string().max(80)),
  world: lenient(z.string().regex(/^[a-z0-9][a-z0-9_:-]{0,39}$/)),
  needs_look: lenient(z.literal(true)),
})
export type BackgroundsSearch = z.infer<typeof backgroundsSearchSchema>

export function validateBackgroundsSearch(input: Record<string, unknown>): BackgroundsSearch {
  return backgroundsSearchSchema.parse(input)
}

/**
 * An INDEX route (trailing slash). As a plain `/backgrounds` it would lose to the editor's splat
 * (`/backgrounds/$` also matches `/backgrounds`, with an empty uid, and the deeper route wins);
 * an index segment beats a splat. Links still say `to="/backgrounds"`.
 */
export const BACKGROUNDS_ROUTE_ID = '/backgrounds/' as const
const route = getRouteApi(BACKGROUNDS_ROUTE_ID)

export function BackgroundsRoutePage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const onSearchChange = useCallback(
    (next: BackgroundsSearchState) => void navigate({ search: { q: next.q, world: next.world, needs_look: next.needs_look ? true : undefined }, replace: true }),
    [navigate],
  )
  return <BackgroundsPage q={search.q ?? ''} world={search.world} needs_look={search.needs_look} onSearchChange={onSearchChange} />
}
